import {
  Card,
  CardProps,
  Center,
  Divider,
  Flex,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { Patient, Reference } from '@medplum/fhirtypes';
import { useResource } from '@medplum/react-hooks';
import {
  IconGenderFemale,
  IconGenderMale,
  IconStethoscope,
} from '@tabler/icons-react';
import { FC } from 'react';
import { usePatientResources } from '@providers/usePatientResources';
import { useTranslation } from 'react-i18next';
import { Allergies } from './allergies/Allergies';
import { ProblemList } from './conditions/ProblemList';
import { Procedures } from './procedures/Procedures';
import { Medications } from './medications/Medications';
import { TherapeuticInterventions } from './therapeutic-interventions/TherapeuticInterventions';
import { GeneticDisease } from './genetic-disease/GeneticDisease';
import { HpoSummary } from './hpos/HpoSummary';

// -----------------------------------------------------------------------------
export interface PatientSummaryProps extends Omit<CardProps, 'children'> {
  readonly patient: Patient | Reference<Patient>;
  readonly allergiesDisabled?: boolean;
  readonly geneticDiseasesDisabled?: boolean;
  readonly problemsDisabled?: boolean;
  readonly medicationsDisabled?: boolean;
  readonly proceduresDisabled?: boolean;
  readonly therapeuticInterventionsDisabled?: boolean;
  readonly headerDisabled?: boolean;
}

// -----------------------------------------------------------------------------
type IconType = typeof IconGenderFemale;

// -----------------------------------------------------------------------------
const mapGenderGreek = (gender?: string): string => {
  const { t } = useTranslation(['patient-pages']);
  switch (gender) {
    case 'female':
      return t('summaryPage.summary.gender.female' as const);
    case 'male':
      return t('summaryPage.summary.gender.male' as const);
    case 'other':
      return t('summaryPage.summary.gender.other' as const);
    case 'unknown':
      return t('summaryPage.summary.gender.unknown' as const);
    default:
      return '-';
  }
};

// -----------------------------------------------------------------------------
const getGenderIcon = (patient?: Patient): IconType | undefined => {
  switch (patient?.gender) {
    case 'female':
      return IconGenderFemale;
    case 'male':
      return IconGenderMale;
    default:
      return undefined;
  }
};

// -----------------------------------------------------------------------------
export const PatientSummary: FC<PatientSummaryProps> = ({
  patient: propsPatient,
  allergiesDisabled = false,
  geneticDiseasesDisabled = false,
  problemsDisabled = false,
  medicationsDisabled = false,
  proceduresDisabled = false,
  therapeuticInterventionsDisabled = false,
  headerDisabled = false,
  ...cardProps
}) => {
  const patient = useResource(propsPatient);
  const { t } = useTranslation(['patient-pages']);

  // Get all resources from context (real-time updates via subscriptions)
  const { conditions, allergies, medications, procedures, loading } =
    usePatientResources();

  // ---------------------------------------------------------------------------
  if (!patient) {
    return null;
  }

  // ---------------------------------------------------------------------------
  const GenderIconComponent = getGenderIcon(patient);
  const isLoading =
    loading.conditions ||
    loading.allergies ||
    loading.medications ||
    loading.procedures;

  // ---------------------------------------------------------------------------
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder {...cardProps}>
      {!headerDisabled && (
        <Paper withBorder p="md" mb={'md'}>
          <Group wrap="nowrap" justify="space-evenly">
            <Flex justify="center" align="center" direction="column" gap={0}>
              <IconStethoscope size={24} color="gray" />
              <Text fz="xs" style={{ whiteSpace: 'nowrap' }}>
                {patient?.generalPractitioner?.[0]?.display ??
                  t('summaryPage.summary.noProvider' as const)}
              </Text>
            </Flex>
            {GenderIconComponent && (
              <Flex justify="center" align="center" direction="column" gap={0}>
                <GenderIconComponent size={24} color="gray" />
                <Text fz="xs" style={{ whiteSpace: 'nowrap' }}>
                  {mapGenderGreek(patient.gender)}
                </Text>
              </Flex>
            )}
          </Group>
        </Paper>
      )}
      <Stack gap="xs">
        {!isLoading && (
          <>
            <Text fz="lg" fw={700} mb="xs">
              {t('summaryPage.summary.patientProfile.title')}
            </Text>

            {!problemsDisabled && (
              <>
                <ProblemList problems={conditions.problems} />
                <Divider />
              </>
            )}
            {!geneticDiseasesDisabled && (
              <>
                <GeneticDisease
                  patient={patient}
                  geneticDiseases={conditions.geneticDiseases}
                />
                <Divider />
              </>
            )}
            <HpoSummary hpoConditions={conditions.hpoConditions} />
            <Divider />

            {!medicationsDisabled && (
              <>
                <Medications medications={medications.medications} />
                <Divider />
              </>
            )}
            {!therapeuticInterventionsDisabled && (
              <>
                <TherapeuticInterventions
                  patient={patient}
                  therapeuticInterventions={procedures.therapeuticInterventions}
                />
                <Divider />
              </>
            )}
            {!proceduresDisabled && (
              <>
                <Procedures procedures={procedures.procedures} />
                <Divider />
              </>
            )}
            {!allergiesDisabled && (
              <Allergies allergies={allergies.allergies} />
            )}
          </>
        )}
        {isLoading && (
          <Center>
            <Loader size="sm" />
          </Center>
        )}
      </Stack>
    </Card>
  );
};
